import React from "react";
import { motion, useReducedMotion } from "framer-motion";

import janaschool from "./groupstudents.jpg";
import schoolHistoryImg from "./students.jpg";

import Footer from "./Footer";

import {
    FaBuilding,
    FaSchool,
    FaArrowRight,
    FaGraduationCap,
    FaBookOpen,
    FaStar,
    FaCheckCircle,
    FaLightbulb,
} from "react-icons/fa";

const CampusPage = () => {
    const shouldReduceMotion = useReducedMotion();

    const fadeUp = {
        hidden: {
            opacity: 0,
            y: shouldReduceMotion ? 0 : 35,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: shouldReduceMotion ? 0 : 0.75,
                ease: "easeOut",
            },
        },
    };

    const campusFeatures = [
        {
            icon: <FaBookOpen />,
            title: "Learning-Focused",
            description:
                "Supportive classrooms and learning spaces that encourage curiosity, academic progress, and discovery.",
        },
        {
            icon: <FaLightbulb />,
            title: "Innovation & Creativity",
            description:
                "An environment that encourages students to think critically, share ideas, and develop practical skills.",
        },
        {
            icon: <FaGraduationCap />,
            title: "Whole-Student Growth",
            description:
                "Opportunities to develop confidence, responsibility, leadership, and a lifelong love of learning.",
        },
    ];

    const styles = `
        :root {
            --campus-purple: #5B21B6;
            --campus-purple-dark: #321064;
            --campus-purple-deep: #210A45;
            --campus-purple-light: #F3EEFF;
            --campus-gold: #F6C944;
            --campus-gold-dark: #D9A817;
            --campus-text: #29233A;
            --campus-muted: #716A80;
            --campus-white: #FFFFFF;
            --campus-background: #FBF9FF;
            --campus-border: #EAE3F5;
        }

        .jana-campus-page {
            width: 100%;
            overflow: hidden;
            color: var(--campus-text);
            background: var(--campus-white);
            font-family: inherit;
        }

        .jana-campus-page *,
        .jana-campus-page *::before,
        .jana-campus-page *::after {
            box-sizing: border-box;
        }

        .jana-campus-page a {
            text-decoration: none;
        }

        .jana-campus-hero {
            position: relative;
            display: flex;
            align-items: center;
            min-height: 590px;
            min-height: clamp(440px, 60vw, 690px);
            padding: 100px 7%;
            isolation: isolate;
            background-color: var(--campus-purple-deep);
            background-position: center;
            background-size: cover;
        }

        .jana-campus-hero::before {
            position: absolute;
            z-index: -1;
            inset: 0;
            content: "";
            background:
                linear-gradient(
                    100deg,
                    rgba(31, 8, 66, 0.94) 0%,
                    rgba(55, 18, 105, 0.82) 46%,
                    rgba(49, 16, 100, 0.36) 100%
                );
        }

        .jana-campus-hero::after {
            position: absolute;
            z-index: -1;
            right: -100px;
            bottom: -220px;
            width: 520px;
            height: 520px;
            content: "";
            border: 1px solid rgba(246, 201, 68, 0.32);
            border-radius: 50%;
            box-shadow:
                0 0 0 45px rgba(246, 201, 68, 0.045),
                0 0 0 90px rgba(246, 201, 68, 0.035);
        }

        .jana-campus-hero-inner {
            width: 100%;
            max-width: 1240px;
            margin: 0 auto;
        }

        .jana-campus-hero-content {
            max-width: 760px;
        }

        .jana-campus-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 24px;
            color: var(--campus-gold);
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 3px;
            text-transform: uppercase;
        }

        .jana-campus-eyebrow::before {
            width: 34px;
            height: 2px;
            content: "";
            background: var(--campus-gold);
        }

        .jana-campus-hero h1 {
            margin: 0 0 24px;
            color: var(--campus-white);
            font-size: clamp(48px, 7vw, 88px);
            font-weight: 850;
            letter-spacing: -3px;
            line-height: 1.03;
        }

        .jana-campus-hero h1 span {
            display: block;
            color: var(--campus-gold);
        }

        .jana-campus-hero-description {
            max-width: 570px;
            margin: 0;
            color: rgba(255, 255, 255, 0.86);
            font-size: clamp(16px, 1.7vw, 19px);
            line-height: 1.9;
        }

        .jana-campus-hero-actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 16px;
            margin-top: 34px;
        }

        .jana-campus-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            min-height: 52px;
            padding: 15px 24px;
            border: 1px solid transparent;
            border-radius: 9px;
            font-size: 14px;
            font-weight: 800;
            cursor: pointer;
            transition:
                transform 0.25s ease,
                background 0.25s ease,
                box-shadow 0.25s ease;
        }

        .jana-campus-button:hover {
            transform: translateY(-3px);
        }

        .jana-campus-button-primary {
            color: var(--campus-purple-deep);
            background: var(--campus-gold);
            box-shadow: 0 10px 28px rgba(246, 201, 68, 0.2);
        }

        .jana-campus-button-primary:hover {
            background: #FFDA68;
            box-shadow: 0 14px 32px rgba(246, 201, 68, 0.3);
        }

        .jana-campus-button-outline {
            color: var(--campus-white);
            border-color: rgba(255, 255, 255, 0.45);
            background: rgba(255, 255, 255, 0.07);
        }

        .jana-campus-button-outline:hover {
            background: rgba(255, 255, 255, 0.15);
        }

        .jana-campus-hero-note {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 30px;
            color: rgba(255, 255, 255, 0.76);
            font-size: 13px;
        }

        .jana-campus-hero-note svg {
            color: var(--campus-gold);
        }

        .jana-campus-intro {
            position: relative;
            padding: 110px 7%;
            background:
                radial-gradient(
                    circle at 5% 15%,
                    rgba(120, 74, 190, 0.07),
                    transparent 27%
                ),
                var(--campus-background);
        }

        .jana-campus-intro-inner {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 0.92fr);
            align-items: center;
            gap: clamp(45px, 7vw, 100px);
            width: 100%;
            max-width: 1240px;
            margin: 0 auto;
        }

        .jana-campus-visual {
            position: relative;
            min-width: 0;
            padding: 0 24px 28px 0;
        }

        .jana-campus-image-frame {
            position: relative;
            overflow: hidden;
            min-height: 360px;
            border-radius: 22px;
            background: #E5DDF1;
            box-shadow: 0 25px 65px rgba(48, 20, 84, 0.14);
        }

        .jana-campus-image-frame::after {
            position: absolute;
            inset: 0;
            content: "";
            pointer-events: none;
            background: linear-gradient(
                180deg,
                transparent 55%,
                rgba(28, 8, 56, 0.18) 100%
            );
        }

        .jana-campus-image-frame img {
            display: block;
            width: 100%;
            height: 460px;
            object-fit: cover;
            transition: transform 0.7s ease;
        }

        .jana-campus-image-frame:hover img {
            transform: scale(1.045);
        }

        .jana-campus-image-accent {
            position: absolute;
            z-index: 0;
            top: -18px;
            left: -18px;
            width: 120px;
            height: 120px;
            border-top: 5px solid var(--campus-gold);
            border-left: 5px solid var(--campus-gold);
            border-radius: 18px 0 0 0;
        }

        .jana-campus-image-badge {
            position: absolute;
            right: 0;
            bottom: 0;
            z-index: 2;
            display: flex;
            align-items: center;
            gap: 14px;
            max-width: calc(100% - 20px);
            padding: 20px 24px;
            border: 1px solid rgba(255, 255, 255, 0.15);
            border-radius: 14px;
            color: var(--campus-white);
            background: var(--campus-purple-dark);
            box-shadow: 0 14px 35px rgba(39, 13, 76, 0.22);
        }

        .jana-campus-image-badge-icon {
            display: grid;
            flex-shrink: 0;
            width: 46px;
            height: 46px;
            place-items: center;
            border-radius: 12px;
            color: var(--campus-purple-deep);
            background: var(--campus-gold);
            font-size: 20px;
        }

        .jana-campus-image-badge strong {
            display: block;
            margin-bottom: 5px;
            font-size: 14px;
        }

        .jana-campus-image-badge span {
            display: block;
            color: rgba(255, 255, 255, 0.75);
            font-size: 12px;
            line-height: 1.5;
        }

        .jana-campus-section-label {
            display: inline-block;
            margin-bottom: 17px;
            color: var(--campus-purple);
            font-size: 12px;
            font-weight: 850;
            letter-spacing: 2.5px;
            text-transform: uppercase;
        }

        .jana-campus-section-title {
            margin: 0 0 22px;
            color: var(--campus-purple-deep);
            font-size: clamp(32px, 4vw, 48px);
            font-weight: 850;
            letter-spacing: -1.5px;
            line-height: 1.17;
        }

        .jana-campus-section-title span {
            color: var(--campus-purple);
        }

        .jana-campus-copy {
            margin: 0 0 18px;
            color: var(--campus-muted);
            font-size: 15px;
            line-height: 1.95;
        }

        .jana-campus-checklist {
            display: grid;
            gap: 14px;
            margin: 27px 0 32px;
            padding: 0;
            list-style: none;
        }

        .jana-campus-checklist li {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            color: #403750;
            font-size: 14px;
            line-height: 1.7;
        }

        .jana-campus-checklist svg {
            flex-shrink: 0;
            margin-top: 3px;
            color: var(--campus-purple);
            font-size: 17px;
        }

        .jana-campus-content-button {
            color: var(--campus-white);
            background: var(--campus-purple);
            box-shadow: 0 10px 24px rgba(91, 33, 182, 0.18);
        }

        .jana-campus-content-button:hover {
            background: var(--campus-purple-dark);
            box-shadow: 0 14px 30px rgba(91, 33, 182, 0.25);
        }

        .jana-campus-features {
            padding: 100px 7%;
            background: var(--campus-white);
        }

        .jana-campus-features-inner {
            width: 100%;
            max-width: 1240px;
            margin: 0 auto;
        }

        .jana-campus-features-heading {
            max-width: 690px;
            margin: 0 auto 52px;
            text-align: center;
        }

        .jana-campus-features-heading .jana-campus-section-title {
            margin-bottom: 16px;
        }

        .jana-campus-features-heading p {
            margin: 0;
            color: var(--campus-muted);
            font-size: 15px;
            line-height: 1.9;
        }

        .jana-campus-feature-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 24px;
        }

        .jana-campus-feature-card {
            position: relative;
            overflow: hidden;
            padding: 34px 30px;
            border: 1px solid var(--campus-border);
            border-radius: 18px;
            background: var(--campus-white);
            box-shadow: 0 8px 28px rgba(44, 21, 75, 0.035);
            transition:
                transform 0.3s ease,
                border-color 0.3s ease,
                box-shadow 0.3s ease;
        }

        .jana-campus-feature-card::after {
            position: absolute;
            right: -30px;
            bottom: -45px;
            width: 120px;
            height: 120px;
            border-radius: 50%;
            content: "";
            background: rgba(246, 201, 68, 0.11);
            transition: transform 0.35s ease;
        }

        .jana-campus-feature-card:hover {
            transform: translateY(-7px);
            border-color: #D7C4F5;
            box-shadow: 0 20px 45px rgba(56, 23, 99, 0.1);
        }

        .jana-campus-feature-card:hover::after {
            transform: scale(1.35);
        }

        .jana-campus-feature-icon {
            display: grid;
            width: 58px;
            height: 58px;
            margin-bottom: 24px;
            place-items: center;
            border-radius: 16px;
            color: var(--campus-purple);
            background: var(--campus-purple-light);
            font-size: 24px;
        }

        .jana-campus-feature-card h3 {
            margin: 0 0 13px;
            color: var(--campus-purple-deep);
            font-size: 19px;
            font-weight: 800;
        }

        .jana-campus-feature-card p {
            position: relative;
            z-index: 1;
            margin: 0;
            color: var(--campus-muted);
            font-size: 14px;
            line-height: 1.9;
        }

        .jana-campus-cta {
            position: relative;
            overflow: hidden;
            margin: 0 7% 100px;
            padding: 58px clamp(28px, 6vw, 75px);
            border-radius: 22px;
            color: var(--campus-white);
            background:
                radial-gradient(
                    circle at 92% 10%,
                    rgba(246, 201, 68, 0.2),
                    transparent 28%
                ),
                linear-gradient(
                    120deg,
                    var(--campus-purple-deep),
                    var(--campus-purple)
                );
        }

        .jana-campus-cta::after {
            position: absolute;
            top: -130px;
            right: -70px;
            width: 300px;
            height: 300px;
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 50%;
            content: "";
            box-shadow: 0 0 0 35px rgba(255, 255, 255, 0.035);
            pointer-events: none;
        }

        .jana-campus-cta-inner {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 30px;
            max-width: 1090px;
            margin: 0 auto;
        }

        .jana-campus-cta h2 {
            max-width: 650px;
            margin: 0 0 12px;
            color: var(--campus-white);
            font-size: clamp(27px, 3.5vw, 40px);
            font-weight: 850;
            letter-spacing: -1px;
            line-height: 1.2;
        }

        .jana-campus-cta p {
            max-width: 620px;
            margin: 0;
            color: rgba(255, 255, 255, 0.79);
            font-size: 14px;
            line-height: 1.8;
        }

        .jana-campus-cta .jana-campus-button {
            flex-shrink: 0;
        }

        .jana-campus-button:focus-visible {
            outline: 3px solid var(--campus-gold);
            outline-offset: 4px;
        }

        @media (max-width: 1000px) {
            .jana-campus-intro-inner {
                gap: 45px;
            }

            .jana-campus-image-frame img {
                height: 410px;
            }

            .jana-campus-feature-grid {
                gap: 16px;
            }

            .jana-campus-feature-card {
                padding: 28px 22px;
            }
        }

        @media (max-width: 760px) {
            .jana-campus-hero {
                min-height: 520px;
                padding: 85px 6%;
                background-position: 58% center;
            }

            .jana-campus-hero h1 {
                letter-spacing: -2px;
            }

            .jana-campus-intro {
                padding: 75px 6%;
            }

            .jana-campus-intro-inner {
                grid-template-columns: 1fr;
                gap: 48px;
            }

            .jana-campus-visual {
                max-width: 600px;
                width: 100%;
                margin: 0 auto;
            }

            .jana-campus-image-frame img {
                height: 400px;
            }

            .jana-campus-section-title {
                font-size: 36px;
            }

            .jana-campus-features {
                padding: 75px 6%;
            }

            .jana-campus-feature-grid {
                grid-template-columns: 1fr;
                gap: 18px;
            }

            .jana-campus-feature-card {
                padding: 30px;
            }

            .jana-campus-cta {
                margin: 0 6% 75px;
                padding: 42px 28px;
            }

            .jana-campus-cta-inner {
                align-items: flex-start;
                flex-direction: column;
            }
        }

        @media (max-width: 480px) {
            .jana-campus-hero {
                min-height: 490px;
                padding: 70px 22px;
            }

            .jana-campus-eyebrow {
                font-size: 10px;
                letter-spacing: 2px;
            }

            .jana-campus-hero h1 {
                font-size: 46px;
            }

            .jana-campus-hero-description {
                font-size: 15px;
                line-height: 1.8;
            }

            .jana-campus-hero-actions {
                align-items: stretch;
                flex-direction: column;
            }

            .jana-campus-button {
                width: 100%;
            }

            .jana-campus-hero-note {
                align-items: flex-start;
                font-size: 12px;
                line-height: 1.7;
            }

            .jana-campus-intro {
                padding: 65px 22px;
            }

            .jana-campus-visual {
                padding-right: 12px;
                padding-bottom: 22px;
            }

            .jana-campus-image-frame {
                min-height: 280px;
                border-radius: 16px;
            }

            .jana-campus-image-frame img {
                height: 310px;
            }

            .jana-campus-image-accent {
                top: -10px;
                left: -10px;
                width: 75px;
                height: 75px;
                border-width: 4px;
            }

            .jana-campus-image-badge {
                gap: 10px;
                padding: 13px;
            }

            .jana-campus-image-badge-icon {
                width: 38px;
                height: 38px;
                font-size: 17px;
            }

            .jana-campus-image-badge strong {
                font-size: 12px;
            }

            .jana-campus-image-badge span {
                font-size: 10px;
            }

            .jana-campus-section-title {
                font-size: 32px;
            }

            .jana-campus-copy {
                font-size: 14px;
            }

            .jana-campus-features {
                padding: 65px 22px;
            }

            .jana-campus-features-heading {
                margin-bottom: 35px;
            }

            .jana-campus-feature-card {
                padding: 25px 22px;
            }

            .jana-campus-cta {
                margin: 0 22px 65px;
                padding: 35px 23px;
                border-radius: 17px;
            }

            .jana-campus-cta h2 {
                font-size: 29px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .jana-campus-page *,
            .jana-campus-page *::before,
            .jana-campus-page *::after {
                scroll-behavior: auto !important;
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
            }
        }
    `;

    return (
        <div className="jana-campus-page">
            <style>{styles}</style>

            {/* Hero Section */}
            <section
                className="jana-campus-hero"
                style={{
                    backgroundImage: `url("${janaschool}")`,
                }}
                aria-labelledby="jana-campus-hero-title"
            >
                <div className="jana-campus-hero-inner">
                    <motion.div
                        className="jana-campus-hero-content"
                        initial={{
                            opacity: 0,
                            y: shouldReduceMotion ? 0 : 35,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            duration: shouldReduceMotion ? 0 : 0.85,
                            ease: "easeOut",
                        }}
                    >
                        <span className="jana-campus-eyebrow">
                            Our Environment
                        </span>

                        <h1 id="jana-campus-hero-title">
                            A Place to
                            <span>Learn & Thrive.</span>
                        </h1>

                        <p className="jana-campus-hero-description">
                            Discover a welcoming school environment where
                            knowledge grows, creativity flourishes, and every
                            student is encouraged to reach their full potential.
                        </p>

                        <div className="jana-campus-hero-actions">
                            <a
                                className="jana-campus-button jana-campus-button-primary"
                                href="#campus-information"
                            >
                                Discover Our Campus
                                <FaArrowRight aria-hidden="true" />
                            </a>

                            <a
                                className="jana-campus-button jana-campus-button-outline"
                                href="#campus-features"
                            >
                                Explore Our Facilities
                            </a>
                        </div>

                        <div className="jana-campus-hero-note">
                            <FaStar aria-hidden="true" />
                            A welcoming environment for learning, growth, and
                            achievement.
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Campus Information */}
            <section
                className="jana-campus-intro"
                id="campus-information"
                aria-labelledby="jana-campus-intro-title"
            >
                <div className="jana-campus-intro-inner">
                    <motion.div
                        className="jana-campus-visual"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <div className="jana-campus-image-accent" />

                        <div className="jana-campus-image-frame">
                            <img
                                src={schoolHistoryImg}
                                alt="Students learning and engaging in the school environment"
                                loading="lazy"
                            />
                        </div>

                        <div className="jana-campus-image-badge">
                            <div className="jana-campus-image-badge-icon">
                                <FaSchool aria-hidden="true" />
                            </div>

                            <div>
                                <strong>Our Learning Community</strong>
                                <span>
                                    Growing together through education
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className="jana-campus-content"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.75, delay: 0.1 }}
                    >
                        <span className="jana-campus-section-label">
                            Welcome to Our Campus
                        </span>

                        <h2
                            className="jana-campus-section-title"
                            id="jana-campus-intro-title"
                        >
                            An Environment Built for
                            <span> Excellence</span>
                        </h2>

                        <p className="jana-campus-copy">
                            A positive school environment helps students feel
                            supported, develop confidence, and build the
                            foundation for lifelong learning. Our campus is
                            intended to be a welcoming place where students
                            can learn, collaborate, and grow.
                        </p>

                        <p className="jana-campus-copy">
                            Through engaging classroom experiences, meaningful
                            relationships, and opportunities to explore new
                            ideas, we strive to help every learner discover
                            their strengths and prepare for the future.
                        </p>

                        <ul className="jana-campus-checklist">
                            <li>
                                <FaCheckCircle aria-hidden="true" />
                                Supportive and engaging learning experiences
                            </li>
                            <li>
                                <FaCheckCircle aria-hidden="true" />
                                Opportunities for creativity and critical thinking
                            </li>
                            <li>
                                <FaCheckCircle aria-hidden="true" />
                                A focus on character, responsibility, and growth
                            </li>
                        </ul>

                        <a
                            className="jana-campus-button jana-campus-content-button"
                            href="#campus-features"
                        >
                            Explore Our Environment
                            <FaArrowRight aria-hidden="true" />
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Campus Features */}
            <section
                className="jana-campus-features"
                id="campus-features"
                aria-labelledby="jana-campus-features-title"
            >
                <div className="jana-campus-features-inner">
                    <motion.div
                        className="jana-campus-features-heading"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <span className="jana-campus-section-label">
                            What Makes Our Campus Special
                        </span>

                        <h2
                            className="jana-campus-section-title"
                            id="jana-campus-features-title"
                        >
                            More Than a School.
                            <span> A Place to Grow.</span>
                        </h2>

                        <p>
                            We believe meaningful education goes beyond the
                            classroom. Our learning environment supports
                            academic development, imagination, and the
                            personal growth of every student.
                        </p>
                    </motion.div>

                    <div className="jana-campus-feature-grid">
                        {campusFeatures.map((feature, index) => (
                            <motion.article
                                className="jana-campus-feature-card"
                                key={feature.title}
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: shouldReduceMotion ? 0 : 0.6,
                                    delay: shouldReduceMotion ? 0 : index * 0.12,
                                }}
                            >
                                <div className="jana-campus-feature-icon">
                                    {feature.icon}
                                </div>

                                <h3>{feature.title}</h3>

                                <p>{feature.description}</p>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Closing Call to Action */}
            <motion.section
                className="jana-campus-cta"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <div className="jana-campus-cta-inner">
                    <div>
                        <h2>
                            Every Great Future Begins with a Place to Learn.
                        </h2>

                        <p>
                            Explore our school community and discover an
                            environment committed to learning, personal
                            development, and opportunity.
                        </p>
                    </div>

                    <a
                        className="jana-campus-button jana-campus-button-primary"
                        href="#campus-information"
                    >
                        Discover More
                        <FaArrowRight aria-hidden="true" />
                    </a>
                </div>
            </motion.section>

            <Footer />
        </div>
    );
};

export default CampusPage;
